import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus } from 'lucide-react';
import { WebsiteFilter } from '@/components/website/WebsiteFilter';
import { IdeaTile } from '@/components/website/IdeaTile';
import { IdeaModal } from '@/components/website/IdeaModal';
import { TaskModal } from '@/components/website/TaskModal';
import { useWebsiteIdeas } from '@/hooks/useWebsiteIdeas';
import { useWebsiteTasks } from '@/hooks/useWebsiteTasks';
import { useAuth } from '@/hooks/useAuth';
import type { WebsiteName, WebsiteIdea, IdeaStatus } from '@/types/website';
import { IDEA_STATUS_LABELS } from '@/types/website';

export default function CRMWebsiteIdeas() {
  const { user } = useAuth();
  const [websiteFilter, setWebsiteFilter] = useState<WebsiteName | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<IdeaStatus | 'all'>('all');
  const [sortBy, setSortBy] = useState<'impact' | 'date'>('impact');
  const [ideaModalOpen, setIdeaModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState<WebsiteIdea | null>(null);

  const filter = websiteFilter === 'all' ? undefined : websiteFilter;
  const { ideas, createIdea, updateIdea, deleteIdea } = useWebsiteIdeas(filter);
  const { createTask } = useWebsiteTasks();

  let filtered = ideas.filter(i => statusFilter === 'all' || i.status === statusFilter);
  if (sortBy === 'date') {
    filtered = [...filtered].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }

  const handleConvertToTask = (idea: WebsiteIdea) => {
    setSelectedIdea(idea);
    setTaskModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Ideeënbank</h1>
        <div className="flex items-center gap-3">
          <WebsiteFilter value={websiteFilter} onChange={setWebsiteFilter} />
          <Button onClick={() => { setSelectedIdea(null); setIdeaModalOpen(true); }}>
            <Plus className="h-4 w-4 mr-1" />
            Nieuw idee
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as IdeaStatus | 'all')}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            {Object.entries(IDEA_STATUS_LABELS).map(([key, label]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={(v) => setSortBy(v as 'impact' | 'date')}>
          <SelectTrigger className="w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="impact">Impact (hoog eerst)</SelectItem>
            <SelectItem value="date">Datum (nieuwste)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.map(idea => (
          <IdeaTile
            key={idea.id}
            idea={idea}
            canEdit={idea.created_by === user?.id}
            onEdit={() => { setSelectedIdea(idea); setIdeaModalOpen(true); }}
            onDelete={() => deleteIdea.mutate(idea.id)}
            onConvertToTask={() => handleConvertToTask(idea)}
          />
        ))}
        {filtered.length === 0 && (
          <p className="text-muted-foreground col-span-full text-center py-8">Geen ideeën gevonden</p>
        )}
      </div>

      <IdeaModal
        open={ideaModalOpen}
        onOpenChange={setIdeaModalOpen}
        idea={selectedIdea}
        onSave={(data) => data.id ? updateIdea.mutate(data as WebsiteIdea) : createIdea.mutate(data as any)}
        onDelete={(id) => deleteIdea.mutate(id)}
        defaultWebsite={websiteFilter === 'all' ? undefined : websiteFilter}
      />

      <TaskModal
        open={taskModalOpen}
        onOpenChange={setTaskModalOpen}
        task={null}
        onSave={(task) => {
          createTask.mutate(task as any);
          if (selectedIdea) updateIdea.mutate({ id: selectedIdea.id, status: 'goedgekeurd' });
        }}
        defaultWebsite={selectedIdea?.website}
      />
    </div>
  );
}